/* RS v0 — the phone→TV RESET payload, and the device identity it is scoped to.

   Separate from wcCode.js on purpose: a reset code and a WiFi-credentials code
   share only the envelope they are written in (pipeEnvelope.js). WC hands the
   Hearth a network to join; RS tells it to sign out and forget who it is. The
   only thing they have in common is the escaping rule, which is why that rule
   lives in a third module rather than in either of them.

   Wire contract: docs/imp-docs/no-network-wifi/QR-WC-FORMAT.md §7 —

     |RS|0|<familyIdPrefix>|

   This format is frozen; changes are a version bump, never a mutation. */

import { escapeEnvelopeField } from './pipeEnvelope';

/* `familyIdPrefix` is the first 8 characters of the Hearth's familyID, taken
   from the `fid` parameter the device put in its own QR. The device accepts the
   code only if that prefix matches the one it advertised, so a code minted for
   one Hearth cannot reset another and no universal reset code can exist.

   It carries no expiry and no nonce: the same code resets the same Hearth for
   as long as that Hearth exists. That is a deliberate trade (PLAN decisions
   1-3) — it buys a scoped code at the cost of revocation, and it is why the
   confirmation shown before this is generated is the ONLY warning a customer
   will ever see. */
export function buildRsCode(familyIdPrefix) {
  return `|RS|0|${escapeEnvelopeField(familyIdPrefix)}|`;
}

/* Reads the `fid` parameter from the TV QR's fragment. Returns null when absent
   — which is the normal case for anyone who reached this page without scanning
   a Hearth, and the signal to offer no reset at all: with no device identity
   there is nothing a reset code could safely be scoped to. */
export function parseResetFidFromHash(hash) {
  const raw = (hash || '').replace(/^#/, '');
  const pair = raw.split('&').find((p) => p.startsWith('fid='));
  if (!pair) return null;
  const value = pair.slice('fid='.length);
  if (!value) return null;
  try {
    return decodeURIComponent(value) || null;
  } catch (e) {
    /* Malformed percent-encoding: treat as absent rather than guessing — a
       wrong fid would mint a code the Hearth silently refuses. */
    return null;
  }
}
